/* eslint-disable no-console */
import { useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { getConfig } from '../utils/get-config';

type WSOptions = {
  namespace?: string; // ex.: '/pix' ou '/' (default)
  path?: string;      // ex.: '/socket.io' (default) ou '/api/socket.io'
};

export const useWebSocket = (opts?: WSOptions) => {
  const socketRef = useRef<Socket | null>(null);
  const [socket, setSocket] = useState<Socket | null>(null);

  useEffect(() => {
    const rawUrl =
      (getConfig('REACT_APP_WS_URL') || getConfig('REACT_APP_API_URL')) as string | undefined;
    if (!rawUrl) return;

    if (socketRef.current) {
      setSocket(socketRef.current);
      return;
    }

    let origin: string;
    try {
      const u = new URL(rawUrl);
      origin = u.origin;
    } catch {
      origin = rawUrl.replace(/\/+$/, '');
    }

    const namespace = (opts?.namespace ?? '/').replace(/\/?$/, '') || '/';
    const path = opts?.path ?? '/socket.io';

    const s = io(namespace === '/' ? origin : `${origin}${namespace}`, {
      path,
      transports: ['polling', 'websocket'],
      withCredentials: true,
      reconnection: true,
      forceNew: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 500,
      reconnectionDelayMax: 5000,
      timeout: 15000,
    });

    s.on('connect', () => {});
    s.on('disconnect', () => {});
    s.on('connect_error', (err) => console.error('❗ Connection error', err.message));

    socketRef.current = s;
    setSocket(s);

    return () => {
      s.removeAllListeners();
      s.close();
      socketRef.current = null;
    };
  }, [opts?.namespace, opts?.path]);

  return { socket };
};
